import { useCodeerApi, FetchMethod } from "../useCodeerApi"
import {
  EvaluationCaseSchema,
  EvaluationCaseCountSchema,
  EvaluationCaseListSchema,
  EvaluationCaseLabelSchema,
  CaseEvaluatorInfosByCaseSchema,
  EvaluationResultSchema,
  BulkDeleteResponseSchema,
  TriggerEvaluationResponseSchema,
  EvaluationMetaSchema,
  transToCaseEvaluatorInfosByCase,
} from "./types"
import {
  Response,
  EvaluationCase,
  EvaluationMeta,
  EvaluationCaseEvaluatorPair,
  CaseEvaluatorInfosByCase,
  CaseEvaluatorAssignment,
  EvaluationCaseLabel,
} from "@/types"

// List evaluation cases for an agent (dynamic URL)
export const useListEvaluationCasesApi = (agentId: string | undefined) => {
  return useCodeerApi<Response<EvaluationCaseListSchema>>(
    agentId ? `/eval/agents/${agentId}/cases` : "",
    FetchMethod.Get
  )
}

export const useGetEvaluationCaseCountApi = (agentId: string | undefined) => {
  return useCodeerApi<Response<EvaluationCaseCountSchema>>(
    agentId ? `/eval/agents/${agentId}/cases/count` : "",
    FetchMethod.Get
  )
}

// Get evaluation results by explicit case/evaluator/history targets (batch)
export interface EvaluationResultTargetRequest {
  case_id: string
  evaluator_id: string
  agent_history_id: string
}

export interface GetEvaluationResultsByCasesRequest {
  targets: EvaluationResultTargetRequest[]
  workspace_id: string
  include_output?: boolean // If true, output will be included in the result
  include_reasoning_steps?: boolean
}

export const makeGetEvaluationResultsByCasesRequest = (
  targets: Array<{
    caseId: string
    evaluatorId: string
    agentHistoryId: string
  }>,
  workspaceId: string,
  includeOutput: boolean = true,
  includeReasoningSteps: boolean = false
): GetEvaluationResultsByCasesRequest => {
  return {
    targets: targets.map((target) => ({
      case_id: target.caseId,
      evaluator_id: target.evaluatorId,
      agent_history_id: target.agentHistoryId,
    })),
    workspace_id: workspaceId,
    include_output: includeOutput,
    include_reasoning_steps: includeReasoningSteps,
  }
}

export const useGetEvaluationResultsByCasesApi = () => {
  return useCodeerApi<
    Response<EvaluationResultSchema[]>,
    GetEvaluationResultsByCasesRequest
  >("/eval/results/batch", FetchMethod.Post)
}

export interface CreateEvaluationCaseRequest {
  agent_id: string
  input: string
  expected_output?: string
  note?: string
  rubric?: string
  attachment_ids?: string[]
  evaluators?: Array<{
    evaluator_id: string
    rubric?: string
  }>
  label_ids?: string[]
  meta?: EvaluationMetaSchema
}

export const makeCreateEvaluationCaseRequest = (
  agentId: string,
  input: string,
  expectedOutput?: string,
  rubric?: string,
  attachmentIds?: string[],
  meta?: EvaluationMeta,
  note?: string,
  evaluators?: CaseEvaluatorAssignment[],
  labelIds?: string[]
): CreateEvaluationCaseRequest => {
  return {
    agent_id: agentId,
    input,
    expected_output: expectedOutput,
    note,
    rubric,
    attachment_ids: attachmentIds,
    evaluators: evaluators?.map((assignment) => ({
      evaluator_id: assignment.evaluatorId,
      rubric: assignment.rubric,
    })),
    label_ids: labelIds,
    meta: makeEvaluationMetaSchema(meta),
  }
}

const makeEvaluationMetaSchema = (
  meta: EvaluationMeta | undefined
): EvaluationMetaSchema => {
  if (!meta?.previousConversations) {
    return {}
  }

  return {
    previous_conversations: {
      source_history_id: meta.previousConversations.sourceHistoryId,
      target_conversation_id: meta.previousConversations.targetConversationId,
      previous_conversation_count:
        meta.previousConversations.previousConversationCount,
    },
  }
}

export const useCreateEvaluationCaseApi = () => {
  return useCodeerApi<
    Response<EvaluationCaseSchema>,
    CreateEvaluationCaseRequest
  >("/eval/cases", FetchMethod.Post)
}

export interface TriggerEvaluationRequest {
  case_evaluator_pairs: Array<{
    case_id: string
    evaluator_id: string
  }>
  agent_history_id: string
}

export const makeTriggerEvaluationRequest = (
  caseEvaluatorPairs: EvaluationCaseEvaluatorPair[],
  agentHistoryId: string
): TriggerEvaluationRequest => {
  return {
    case_evaluator_pairs: caseEvaluatorPairs.map((pair) => ({
      case_id: pair.caseId,
      evaluator_id: pair.evaluatorId,
    })),
    agent_history_id: agentHistoryId,
  }
}

export const useTriggerEvaluationApi = () => {
  return useCodeerApi<
    Response<TriggerEvaluationResponseSchema>,
    TriggerEvaluationRequest
  >("/eval/trigger", FetchMethod.Post)
}

export interface StopEvaluationRequest {
  case_id: string
  evaluator_id: string
}

export const makeStopEvaluationRequest = (
  caseId: string,
  evaluatorId: string
): StopEvaluationRequest => {
  return {
    case_id: caseId,
    evaluator_id: evaluatorId,
  }
}

export const useStopEvaluationApi = () => {
  return useCodeerApi<Response<{ message: string }>, StopEvaluationRequest>(
    "/eval/stop",
    FetchMethod.Post
  )
}

// CSV upload custom hook
import { API_PREFIX } from "@/constants"
import useSysConfig from "../useSysConfig"

export function useUploadEvaluationCasesCsv() {
  const config = useSysConfig()
  return async ({
    file,
    agent_id,
    wid,
    evaluator_id,
  }: {
    file: File
    agent_id: string
    wid: string
    evaluator_id?: string
  }) => {
    const formData = new FormData()
    formData.append("file", file)
    // agent_id, wid and evaluator_id go in the query string, not in the form data
    const params = new URLSearchParams({ agent_id, wid })
    if (evaluator_id) params.set("evaluator_id", evaluator_id)
    return fetch(
      `${config.serverRoot}${API_PREFIX}/eval/cases/upload-csv?${params.toString()}`,
      {
        method: "POST",
        body: formData,
        credentials: "include",
      }
    )
  }
}

// Update evaluation case (partial update)
export interface UpdateEvaluationCaseRequest {
  agent_id?: string
  input?: string
  expected_output?: string
  note?: string
  rubric?: string
  attachment_ids?: string[]
  label_ids?: string[]
}

export const makeEvaluationCaseSchema = (
  evaluationCase: Partial<EvaluationCase>
): Partial<EvaluationCaseSchema> => {
  return {
    id: evaluationCase.id,
    agent_id: evaluationCase.agentId,
    workspace_id: evaluationCase.workspaceId,
    input: evaluationCase.input,
    expected_output: evaluationCase.expectedOutput,
    note: evaluationCase.note,
    rubric: evaluationCase.rubric,
    created_at: evaluationCase.createdAt,
    updated_at: evaluationCase.updatedAt,
  }
}

export const makeUpdateEvaluationCaseRequest = (
  evaluationCase: Partial<EvaluationCase> & { labelIds?: string[] }
): UpdateEvaluationCaseRequest => {
  const schema = makeEvaluationCaseSchema(evaluationCase)
  return {
    agent_id: schema.agent_id,
    input: schema.input,
    expected_output: schema.expected_output,
    note: schema.note,
    rubric: schema.rubric,
    attachment_ids: (evaluationCase as { attachmentIds?: string[] })
      .attachmentIds,
    label_ids: evaluationCase.labelIds,
  }
}

export const useUpdateEvaluationCaseApi = (id: string) => {
  return useCodeerApi<
    Response<EvaluationCaseSchema>,
    UpdateEvaluationCaseRequest
  >(`/eval/cases/${id}`, FetchMethod.Put)
}

export interface GetCaseEvaluatorInfosByCasesRequest {
  case_ids: string[]
}

export const makeGetCaseEvaluatorInfosByCasesRequest = (
  caseIds: string[]
): GetCaseEvaluatorInfosByCasesRequest => {
  return {
    case_ids: caseIds,
  }
}

export const useGetCaseEvaluatorInfosByCasesApi = () => {
  return useCodeerApi<
    Response<CaseEvaluatorInfosByCaseSchema[]>,
    GetCaseEvaluatorInfosByCasesRequest
  >("/eval/case-evaluator-infos:batch", FetchMethod.Post)
}

export interface ReplaceCaseEvaluatorInfosRequest {
  evaluators: Array<{
    evaluator_id: string
    rubric?: string
  }>
}

export const makeReplaceCaseEvaluatorInfosRequest = (
  evaluators: CaseEvaluatorAssignment[]
): ReplaceCaseEvaluatorInfosRequest => {
  return {
    evaluators: evaluators.map((assignment) => ({
      evaluator_id: assignment.evaluatorId,
      rubric: assignment.rubric,
    })),
  }
}

export const useReplaceCaseEvaluatorInfosApi = (caseId: string) => {
  return useCodeerApi<
    Response<CaseEvaluatorInfosByCaseSchema>,
    ReplaceCaseEvaluatorInfosRequest
  >(caseId ? `/eval/cases/${caseId}/case-evaluator-infos` : "", FetchMethod.Put)
}

export interface CreateEvaluationCaseLabelRequest {
  name: string
  color?: string
}

export interface UpdateEvaluationCaseLabelRequest {
  name?: string
  color?: string
}

export const useListEvaluationCaseLabelsApi = (workspaceId?: string) => {
  return useCodeerApi<Response<EvaluationCaseLabelSchema[]>>(
    workspaceId ? `/eval/workspaces/${workspaceId}/case-labels` : "",
    FetchMethod.Get
  )
}

export const useCreateEvaluationCaseLabelApi = (workspaceId?: string) => {
  return useCodeerApi<
    Response<EvaluationCaseLabelSchema>,
    CreateEvaluationCaseLabelRequest
  >(
    workspaceId ? `/eval/workspaces/${workspaceId}/case-labels` : "",
    FetchMethod.Post
  )
}

export const useUpdateEvaluationCaseLabelApi = () => {
  return useCodeerApi<
    Response<EvaluationCaseLabelSchema>,
    UpdateEvaluationCaseLabelRequest
  >("", FetchMethod.Put, true, ({ labelId }) => `/eval/case-labels/${labelId}`)
}

export const useDeleteEvaluationCaseLabelApi = () => {
  return useCodeerApi<Response<{ message: string }>, undefined>(
    "",
    FetchMethod.Delete,
    true,
    ({ labelId }) => `/eval/case-labels/${labelId}`
  )
}

export const evaluationCaseLabelResponseToData = (
  label: EvaluationCaseLabelSchema
): EvaluationCaseLabel => ({
  id: label.id,
  workspaceId: label.workspace_id,
  name: label.name,
  color: label.color,
  createdAt: label.created_at,
  updatedAt: label.updated_at,
})

export const caseEvaluatorInfosResponseToData = (
  resp: CaseEvaluatorInfosByCaseSchema[]
): CaseEvaluatorInfosByCase[] => {
  return resp.map(transToCaseEvaluatorInfosByCase)
}

// Set rubric for a case
export interface SetRubricRequest {
  evaluation_case_id: string
  evaluator_id: string
  rubric: string
}

export const makeSetRubricRequest = (
  evaluationCaseId: string,
  evaluatorId: string,
  rubric: string
): SetRubricRequest => {
  return {
    evaluation_case_id: evaluationCaseId,
    evaluator_id: evaluatorId,
    rubric,
  }
}

export const useSetRubricApi = () => {
  return useCodeerApi<Response<EvaluationCaseSchema>, SetRubricRequest>(
    `/eval/rubric`,
    FetchMethod.Post
  )
}

export interface BulkDeleteEvaluationCasesRequest {
  case_ids: string[]
}

export const makeBulkDeleteEvaluationCasesRequest = (
  caseIds: string[]
): BulkDeleteEvaluationCasesRequest => {
  return {
    case_ids: caseIds,
  }
}

export const useBulkDeleteEvaluationCasesApi = () => {
  return useCodeerApi<
    Response<BulkDeleteResponseSchema>,
    BulkDeleteEvaluationCasesRequest
  >("/eval/cases/bulk", FetchMethod.Post)
}
